if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }

        requestAnimationFrame(() => {
          this.logAutoVariantDebug('rAF start');
          const switched = this.selectAvailableVariantOnLoad();
          this.logAutoVariantDebug('rAF done', { switched });
          if (!switched) {
            this.currentVariant = this.getSelectedVariant(this);
            if (this.currentVariant) {
              this.updateMedia(this.currentVariant);
            }
          }
        });
      }

      logAutoVariantDebug(step, data = {}) {
        console.log('[product-info:auto-variant]', step, {
          sectionId: this.sectionId,
          productId: this.productId,
          ...data,
        });
      }

      getProductVariants() {
        const preferredNode = this.querySelector('script[data-product-variants-source="auto-select"]');
        const nodes = preferredNode
          ? [preferredNode]
          : Array.from(this.querySelectorAll('script[data-product-variants]'));

        for (const node of nodes) {
          if (!node?.textContent) continue;

          try {
            const variants = JSON.parse(node.textContent);
            if (!Array.isArray(variants) || !variants.length) continue;

            const compactVariants = variants.filter((variant) => Array.isArray(variant.option_value_ids));
            if (!compactVariants.length) {
              this.logAutoVariantDebug('variants skipped: missing option_value_ids', {
                source: node.getAttribute('data-product-variants-source') || 'unknown',
              });
              continue;
            }

            this.logAutoVariantDebug('variants parsed', {
              count: compactVariants.length,
              source: node.getAttribute('data-product-variants-source') || 'fallback',
              variants: compactVariants,
            });
            return compactVariants;
          } catch (error) {
            this.logAutoVariantDebug('variants parse error', {
              error: error.message,
              rawPreview: node.textContent.slice(0, 200),
            });
          }
        }

        return [];
      }

      getCurrentVariantIdFromUrl() {
        return new URLSearchParams(window.location.search).get('variant');
      }

      isVariantSelectableInDom(variant) {
        const variantSelects = this.variantSelectors;
        if (!variantSelects || !variant?.option_value_ids?.length) return null;

        const states = variant.option_value_ids.map((optionValueId) => {
          const el = variantSelects.querySelector(`[data-option-value-id="${optionValueId}"]`);
          if (!el) return null;
          if (el.tagName === 'OPTION') return !el.disabled;
          return !el.disabled && !el.classList.contains('disabled');
        });

        if (states.some((state) => state === null)) return null;
        return states.every(Boolean);
      }

      isVariantPurchasable(variant, { log = false } = {}) {
        if (!variant) return false;

        let purchasable = false;
        let reason = '';

        if (!variant.available) {
          purchasable = false;
          reason = 'available_false';
        } else if (variant.inventory_management === 'shopify' && typeof variant.inventory_quantity === 'number') {
          const quantityRuleMin = variant.quantity_rule_min ?? 1;

          if (variant.inventory_quantity < 1) {
            purchasable = false;
            reason = 'inventory_quantity_zero';
          } else if (quantityRuleMin > variant.inventory_quantity) {
            purchasable = false;
            reason = 'quantity_rule_min_exceeds_inventory';
          } else {
            purchasable = true;
            reason = 'in_stock';
          }
        } else {
          purchasable = true;
          reason = 'available';
        }

        if (log) {
          this.logAutoVariantDebug('isVariantPurchasable', {
            variantId: variant.id,
            option_value_ids: variant.option_value_ids,
            available: variant.available,
            inventory_policy: variant.inventory_policy,
            inventory_management: variant.inventory_management,
            inventory_quantity: variant.inventory_quantity,
            quantity_rule_min: variant.quantity_rule_min,
            purchasable,
            reason,
          });
        }

        return purchasable;
      }

      isAddToCartDisabled() {
        const addButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        return addButton?.hasAttribute('disabled') ?? false;
      }

      shouldSwitchFromCurrentVariant(currentVariant) {
        const purchasable = this.isVariantPurchasable(currentVariant, { log: true });
        const addToCartDisabled = this.isAddToCartDisabled();

        this.logAutoVariantDebug('shouldSwitchFromCurrentVariant', {
          variantId: currentVariant?.id,
          purchasable,
          addToCartDisabled,
          shouldSwitch: !purchasable || addToCartDisabled,
        });

        return !purchasable || addToCartDisabled;
      }

      findVariantByOptionValueIds(variants, optionValueIds) {
        if (!optionValueIds?.length) return null;

        const exactMatch = variants.find(
          (variant) =>
            variant.option_value_ids?.length === optionValueIds.length &&
            variant.option_value_ids.every((id, index) => String(id) === String(optionValueIds[index])),
        );
        if (exactMatch) return exactMatch;

        return (
          variants.find((variant) =>
            optionValueIds.every((id) =>
              variant.option_value_ids?.some((variantOptionId) => String(variantOptionId) === String(id)),
            ),
          ) || null
        );
      }

      getCurrentVariantFromPage(variants) {
        const urlVariantId = this.getCurrentVariantIdFromUrl();
        if (urlVariantId) {
          const urlVariant = variants.find((variant) => String(variant.id) === String(urlVariantId));
          this.logAutoVariantDebug('current variant from URL', { urlVariantId, urlVariant });
          if (urlVariant) return urlVariant;
        }

        const variantSelects = this.variantSelectors;
        if (variantSelects) {
          const selectedOptionValueIds = variantSelects.selectedOptionValues.filter(Boolean);
          if (selectedOptionValueIds.length) {
            const matchedVariant = this.findVariantByOptionValueIds(variants, selectedOptionValueIds);
            this.logAutoVariantDebug('current variant from picker', {
              selectedOptionValueIds,
              matchedVariant,
            });
            if (matchedVariant) return matchedVariant;
          }
        }

        const formVariantId = this.querySelector(
          `#product-form-${this.sectionId} input[name="id"], #product-form-${this.dataset.section} input[name="id"]`,
        )?.value;
        if (formVariantId) {
          const formVariant = variants.find((variant) => String(variant.id) === String(formVariantId));
          this.logAutoVariantDebug('current variant from form input', { formVariantId, formVariant });
          if (formVariant) return formVariant;
        }

        const selectedVariantJson = this.getSelectedVariant(this);
        if (selectedVariantJson?.id) {
          const jsonVariant = variants.find((variant) => String(variant.id) === String(selectedVariantJson.id));
          this.logAutoVariantDebug('current variant from data-selected-variant', {
            selectedVariantId: selectedVariantJson.id,
            jsonVariant,
          });
          if (jsonVariant) return jsonVariant;
        }

        this.logAutoVariantDebug('current variant fallback first', { variant: variants[0] || null });
        return variants[0] || null;
      }

      selectVariantByOptionValueIds(optionValueIds) {
        const variantSelects = this.variantSelectors;
        if (!variantSelects || !optionValueIds?.length) {
          this.logAutoVariantDebug('selectVariantByOptionValueIds aborted', {
            hasVariantSelects: Boolean(variantSelects),
            optionValueIds,
          });
          return false;
        }

        let lastTarget = null;
        const selectionLog = [];

        optionValueIds.forEach((optionValueId) => {
          const target = variantSelects.querySelector(`[data-option-value-id="${optionValueId}"]`);
          selectionLog.push({
            optionValueId,
            found: Boolean(target),
            tagName: target?.tagName,
            value: target?.value,
          });
          if (!target) return;

          if (target.tagName === 'OPTION') {
            const select = target.closest('select');
            if (!select) return;

            select.value = target.value;
            Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
            target.setAttribute('selected', 'selected');
            lastTarget = select;
            return;
          }

          if (target.matches('input[type="radio"]')) {
            target.checked = true;
            lastTarget = target;
          }
        });

        this.logAutoVariantDebug('selectVariantByOptionValueIds targets', {
          optionValueIds,
          selectionLog,
          lastTarget: lastTarget
            ? { tagName: lastTarget.tagName, id: lastTarget.id, name: lastTarget.name, value: lastTarget.value }
            : null,
        });

        if (!lastTarget) return false;

        lastTarget.dispatchEvent(new Event('change', { bubbles: true }));
        this.logAutoVariantDebug('selectVariantByOptionValueIds dispatched change');
        return true;
      }

      selectAvailableVariantOnLoad() {
        console.group('[product-info:auto-variant] selectAvailableVariantOnLoad');

        const variants = this.getProductVariants();
        if (!variants.length) {
          this.logAutoVariantDebug('aborted: no variants data');
          console.groupEnd();
          return false;
        }

        if (!this.variantSelectors) {
          this.logAutoVariantDebug('aborted: variant-selects not found');
          console.groupEnd();
          return false;
        }

        const currentVariant = this.getCurrentVariantFromPage(variants);
        if (!currentVariant) {
          this.logAutoVariantDebug('aborted: current variant not found');
          console.groupEnd();
          return false;
        }

        if (!this.shouldSwitchFromCurrentVariant(currentVariant)) {
          this.logAutoVariantDebug('aborted: current variant is purchasable', { currentVariant });
          console.groupEnd();
          return false;
        }

        const purchasableVariant = variants.find(
          (variant) => variant.id !== currentVariant.id && this.isVariantPurchasable(variant),
        );
        if (!purchasableVariant) {
          this.logAutoVariantDebug('aborted: no purchasable variant found', {
            variants: variants.map((variant) => ({
              id: variant.id,
              options: variant.options,
              option_value_ids: variant.option_value_ids,
              available: variant.available,
              inventory_policy: variant.inventory_policy,
              inventory_management: variant.inventory_management,
              inventory_quantity: variant.inventory_quantity,
              domSelectable: this.isVariantSelectableInDom(variant),
              purchasable: this.isVariantPurchasable(variant),
            })),
          });
          console.groupEnd();
          return false;
        }

        this.logAutoVariantDebug('switching variant', {
          from: currentVariant,
          to: purchasableVariant,
          option_value_ids: purchasableVariant.option_value_ids,
        });

        const switched = this.selectVariantByOptionValueIds(purchasableVariant.option_value_ids);
        this.logAutoVariantDebug('switch result', { switched });
        console.groupEnd();
        return switched;
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode)
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.currentVariant = variant;
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant
              }
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const parsedUrl = new URL(url, window.location.origin);

        if (variantId) {
          parsedUrl.searchParams.set('variant', variantId);
        } else {
          parsedUrl.searchParams.delete('variant');
        }

        window.history.replaceState({}, '', parsedUrl);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId
          }
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML
          }
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
